import { useEffect, useMemo, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/_core/hooks/useAuth";
import { trpc } from "@/lib/trpc";
import { hasCapability } from "@shared/finance/capabilities";
import { FINANCE_BUDGET_MAX_FILE_BYTES } from "@shared/finance/budgetDocuments";
import { centsFromInput, moneyInput } from "@/finance/moneyInput";
import { useFinanceDialogBounds } from "@/components/finance/financeDialogViewport";
import {
  FinancePageHeader,
  FinanceMetricCard,
  FinanceMetricGrid,
  FinanceSecondaryMetrics,
  FinanceSection,
  FinanceStatusBadge,
  FinanceResponsiveTable,
  FinanceLoadingState,
  FinanceErrorState,
  FinanceAccessDeniedState,
} from "@/components/finance/ui";
import {
  AlertCircle,
  Download,
  Eye,
  ExternalLink,
  FilePlus2,
  History,
  LayoutGrid,
  Loader2,
  PencilLine,
  Plus,
  RefreshCw,
  Table as TableIcon,
  Trash2,
  Upload,
} from "lucide-react";
import { toast } from "sonner";

type EditableLine = {
  lineNumber: number;
  productCatalogId?: number | null;
  productName: string;
  costCategory?: "materials" | "labor" | "other" | null;
  description?: string | null;
  reference?: string | null;
  unit: string;
  quantity: number;
  saleUnitPriceCents?: number | null;
  saleTotalCents: number;
  estimatedUnitCostCents?: number | null;
  estimatedCostCents?: number | null;
  extractionConfidenceBps?: number | null;
  rawText?: string | null;
  reviewStatus: "review_required" | "confirmed";
  warnings: string[];
};

const euro = new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" });
const formatMoney = (value?: number | null) => value == null ? "—" : euro.format(value / 100);
const formatDate = (value?: string | Date | null) => value
  ? new Date(value).toLocaleDateString("es-ES")
  : "—";

const formatExpedienteNumber = (value: unknown, fallback: number) =>
  String(value || fallback).replace(/^\s*exp\.?\s*/i, "");
function budgetStatusLabel(status: string, current: boolean) {
  if (!current || status === "superseded") return "Histórico";
  if (status === "confirmed") return "Confirmado";
  if (status === "failed") return "Revisión bloqueada";
  return "Revisión pendiente";
}

async function postBudgetMultipart(path: "preview" | "confirm", form: FormData) {
  const response = await fetch(`/api/finance/budget-documents/${path}`, {
    method: "POST",
    credentials: "include",
    body: form,
  });
  const payload = await response.json().catch(() => ({ error: "Respuesta no válida" }));
  if (!response.ok) throw new Error(payload.error || `Error HTTP ${response.status}`);
  return payload;
}

export function FinanceBudgetDocumentsView() {
  const { user } = useAuth();
  const canRead = Boolean(user && hasCapability(user.role as any, "finance.read"));
  const canWrite = Boolean(user && hasCapability(user.role as any, "finance.write"));
  const utils = trpc.useUtils();
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [current, setCurrent] = useState("current");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [page, setPage] = useState(1);
  const [viewMode, setViewMode] = useState<"cards" | "table">("table");
  const [uploadInstallationId, setUploadInstallationId] = useState<number | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [detailId, setDetailId] = useState<number | null>(null);
  const detailBounds = useFinanceDialogBounds(detailId !== null);
  const [deleteTarget, setDeleteTarget] = useState<any | null>(null);
  const list = trpc.finance.budgetDocuments.list.useQuery({
    search: search || undefined,
    status: status === "all" ? undefined : status as any,
    current: current === "all" ? undefined : current === "current",
    from: from || undefined,
    to: to || undefined,
    page,
    pageSize: 25,
  }, { enabled: canRead });
  const installations = trpc.installations.list.useQuery(undefined, { enabled: canWrite });
  const detail = trpc.finance.budgetDocuments.getById.useQuery(
    { id: detailId! },
    { enabled: detailId != null },
  );

  const supersede = trpc.finance.budgetDocuments.supersede.useMutation({
    onSuccess: async () => {
      toast.success("La versión dejó de estar vigente");
      await utils.finance.budgetDocuments.list.invalidate();
      setDetailId(null);
    },
    onError: (error) => toast.error(error.message),
  });
  const deleteBudget = trpc.finance.budgetDocuments.delete.useMutation({
    onSuccess: async () => {
      toast.success("Presupuesto eliminado");
      setDeleteTarget(null);
      await utils.finance.budgetDocuments.list.invalidate();
    },
    onError: (error) => toast.error(error.message),
  });


  if (!canRead) {
    return (
      <FinanceAccessDeniedState
        title="Presupuestos no disponible"
        message="Necesitas permiso de lectura financiera para acceder a este módulo."
      />
    );
  }

  const rows = list.data?.rows ?? [];
  const totals = list.data?.totals;

  const secondaryKpis = [
    { label: "Margen previsto", value: totals?.budgetExpectedMarginCents == null ? "Pendiente" : formatMoney(totals.budgetExpectedMarginCents) },
    { label: "Coste real", value: formatMoney(totals?.actualCostCents) },
    { label: "Resultado presupuesto", value: formatMoney(totals?.budgetActualMarginCents) },
    { label: "Contrato vigente", value: formatMoney(totals?.effectiveContractAmountCents) },
    { label: "Margen contractual", value: formatMoney(totals?.contractualActualMarginCents) },
  ];

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <FinancePageHeader
          eyebrow="Finanzas y Contabilidad"
          title="Presupuestos"
          description="Presupuestos comerciales desglosados por expediente y comparados con los costes reales de compra."
          primaryAction={canWrite ? {
            label: "Subir presupuesto",
            icon: Upload,
            onClick: () => { setUploadInstallationId(null); setUploadOpen(true); },
          } : undefined}
          secondaryActions={canWrite ? (
            <Button variant="outline" size="sm" onClick={() => setManualOpen(true)} className="gap-1.5 text-xs font-medium">
              <PencilLine className="h-3.5 w-3.5" />
              <span>Crear manual</span>
            </Button>
          ) : undefined}
        />

        {/* Top 4 primary KPI cards in first row desktop */}
        <FinanceMetricGrid maxColumns={4}>
          <FinanceMetricCard
            title="Presupuestos vigentes"
            amountFormatted={String(totals?.currentCount ?? 0)}
            subtitle="Documentos vigentes"
          />
          <FinanceMetricCard
            title="Base presupuestada"
            amountFormatted={formatMoney(totals?.sourceBaseCents)}
            subtitle="Base imponible"
          />
          <FinanceMetricCard
            title="Total con IVA"
            amountFormatted={formatMoney(totals?.grossCents)}
            subtitle="Importe bruto"
          />
          <FinanceMetricCard
            title="Coste previsto"
            amountFormatted={totals?.estimatedCostCents == null ? "Coste previsto pendiente" : formatMoney(totals.estimatedCostCents)}
            subtitle="Coste estimado total"
          />
        </FinanceMetricGrid>

        <FinanceSecondaryMetrics
          title="Otras métricas de presupuestos"
          metrics={secondaryKpis}
        />

        <Card className="shadow-2xs border-border/70">
          <CardHeader className="pb-3">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <CardTitle className="text-base font-semibold">Registro de presupuestos</CardTitle>
              <div className="flex flex-wrap items-center gap-2">
                <Input
                  className="w-full sm:w-60 h-8 text-xs"
                  placeholder="Buscar expediente, obra, cliente o ref."
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                />
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger className="w-36 h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los estados</SelectItem>
                    <SelectItem value="confirmed">Confirmados</SelectItem>
                    <SelectItem value="review_required">Por revisar</SelectItem>
                    <SelectItem value="failed">Bloqueados</SelectItem>
                    <SelectItem value="superseded">Históricos</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={current} onValueChange={setCurrent}>
                  <SelectTrigger className="w-32 h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="current">Solo vigentes</SelectItem>
                    <SelectItem value="historical">Solo históricos</SelectItem>
                    <SelectItem value="all">Todas</SelectItem>
                  </SelectContent>
                </Select>
                <Input className="w-32 h-8 text-xs" aria-label="Fecha desde" type="date" value={from} onChange={(event) => setFrom(event.target.value)} />
                <Input className="w-32 h-8 text-xs" aria-label="Fecha hasta" type="date" value={to} onChange={(event) => setTo(event.target.value)} />
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => list.refetch()} aria-label="Actualizar">
                  <RefreshCw className="h-3.5 w-3.5" />
                </Button>

                {/* View Mode Selector Toggle: Cards vs Table */}
                <div className="flex items-center rounded-md border border-border/60 bg-muted/40 p-0.5 ml-1">
                  <Button
                    type="button"
                    variant={viewMode === "cards" ? "default" : "ghost"}
                    size="sm"
                    className="h-7 px-2 text-xs gap-1"
                    onClick={() => setViewMode("cards")}
                    aria-label="Vista de tarjetas"
                    title="Vista tarjetas"
                  >
                    <LayoutGrid className="h-3.5 w-3.5" />
                    <span className="hidden sm:inline">Tarjetas</span>
                  </Button>
                  <Button
                    type="button"
                    variant={viewMode === "table" ? "default" : "ghost"}
                    size="sm"
                    className="h-7 px-2 text-xs gap-1"
                    onClick={() => setViewMode("table")}
                    aria-label="Vista de tabla"
                    title="Vista tabla (predeterminada)"
                  >
                    <TableIcon className="h-3.5 w-3.5" />
                    <span className="hidden sm:inline">Tabla</span>
                  </Button>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {list.isLoading ? (
              <FinanceLoadingState cardsCount={4} showTable={false} />
            ) : list.isError ? (
              <FinanceErrorState message={list.error.message} onRetry={() => list.refetch()} />
            ) : rows.length === 0 ? (
              <div className="py-12 text-center border border-dashed rounded-lg bg-muted/10">
                <FilePlus2 className="mx-auto h-8 w-8 text-muted-foreground" />
                <p className="mt-3 font-semibold text-sm text-foreground">Todavía no hay presupuestos documentales</p>
                <p className="mt-1 text-xs text-muted-foreground">Sube un DOCX o PDF, o crea el presupuesto manualmente.</p>
              </div>
            ) : viewMode === "cards" ? (
              /* Visual Cards View */
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {rows.map((row: any) => (
                  <div key={row.id} className="rounded-lg border border-border/80 p-4 shadow-2xs bg-card hover:border-border transition-all flex flex-col justify-between space-y-3">
                    <div className="space-y-2">
                      <div className="flex items-start justify-between gap-2">
                        <div className="space-y-0.5 min-w-0">
                          <span className="text-xs font-mono font-semibold text-primary">Exp. {row.expedienteCode || row.installationId}</span>
                          <h4 className="text-sm font-semibold truncate text-foreground" title={row.clientName || ""}>
                            {row.clientName || "Cliente sin nombre"}
                          </h4>
                        </div>
                        <FinanceStatusBadge status={row.extractionStatus} labelOverride={budgetStatusLabel(row.extractionStatus, row.current)} />
                      </div>

                      <p className="text-xs text-muted-foreground line-clamp-2" title={row.workDescription || ""}>
                        {row.workDescription || "Sin descripción de obra"}
                      </p>

                      <div className="grid grid-cols-2 gap-2 rounded-md bg-muted/30 p-2.5 text-xs">
                        <div>
                          <span className="text-[11px] text-muted-foreground">Fecha: </span>
                          <span className="font-medium">{formatDate(row.budgetDate)}</span>
                        </div>
                        <div>
                          <span className="text-[11px] text-muted-foreground">Versión: </span>
                          <span className="font-medium">v{row.version}</span>
                        </div>
                        <div>
                          <span className="text-[11px] text-muted-foreground">Total: </span>
                          <span className="font-semibold tabular-nums text-foreground">{formatMoney(row.grossCents)}</span>
                        </div>
                        <div>
                          <span className="text-[11px] text-muted-foreground">Coste est.: </span>
                          <span className="font-medium tabular-nums">{row.estimatedCostCents == null ? "Pendiente" : formatMoney(row.estimatedCostCents)}</span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center justify-between gap-1 pt-2 border-t border-border/40">
                      <span className="text-[11px] text-muted-foreground truncate max-w-[120px]" title={row.sourceFileName || "Manual"}>
                        {row.sourceFileName || "Manual"}
                      </span>
                      <div className="flex items-center gap-1 shrink-0">
                        <Button variant="ghost" size="icon" className="h-7 w-7" title="Ver líneas" aria-label="Ver líneas" onClick={() => setDetailId(row.id)}>
                          <Eye className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7" title="Abrir instalación" aria-label="Abrir instalación" onClick={() => { window.location.href = `/installations/${row.installationId}`; }}>
                          <ExternalLink className="h-3.5 w-3.5" />
                        </Button>
                        {row.sourceFileName && (
                          <Button variant="ghost" size="icon" className="h-7 w-7" title="Descargar documento original" aria-label="Descargar documento" onClick={() => window.open(`/api/finance/budget-documents/${row.id}/source`, "_blank")}>
                            <Download className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        {canWrite && row.current && (
                          <Button variant="ghost" size="icon" className="h-7 w-7" title="Subir nueva versión" aria-label="Subir nueva versión" onClick={() => { setUploadInstallationId(row.installationId); setUploadOpen(true); }}>
                            <Upload className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              /* Contained Table View (Default) */
              <FinanceResponsiveTable>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>N.º expediente</TableHead>
                      <TableHead>Cliente</TableHead>
                      <TableHead>Fecha</TableHead>
                      <TableHead>Referencia</TableHead>
                      <TableHead>Documento</TableHead>
                      <TableHead className="text-right">Base</TableHead>
                      <TableHead className="text-right">IVA</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                      <TableHead className="text-right">Coste est.</TableHead>
                      <TableHead className="text-right">Margen est.</TableHead>
                      <TableHead className="text-right">Margen %</TableHead>
                      <TableHead className="text-right">Coste real</TableHead>
                      <TableHead className="text-right">Resultado presupuesto</TableHead>
                      <TableHead className="text-right">Contrato vigente</TableHead>
                      <TableHead className="text-right">Margen contractual</TableHead>
                      <TableHead className="text-right">Desviación</TableHead>
                      <TableHead>Estado</TableHead>
                      <TableHead className="text-right">Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map((row: any) => (
                      <TableRow key={row.id}>
                        <TableCell className="font-mono text-xs font-semibold">{formatExpedienteNumber(row.expedienteCode, row.installationId)}</TableCell>

                        <TableCell><p className="max-w-48 truncate text-xs" title={row.clientName || ""}>{row.clientName || "—"}</p></TableCell>
                        <TableCell className="text-xs">{formatDate(row.budgetDate)}</TableCell>
                        <TableCell className="text-xs">{row.reference}</TableCell>
                        <TableCell className="text-xs">{row.sourceFileName || "Manual"}</TableCell>
                        <TableCell className="text-right text-xs font-mono">{formatMoney(row.sourceBaseCents)}</TableCell>
                        <TableCell className="text-right text-xs font-mono">{formatMoney(row.vatCents)}</TableCell>
                        <TableCell className="text-right text-xs font-mono font-semibold">{formatMoney(row.grossCents)}</TableCell>
                        <TableCell className="text-right text-xs font-mono">{row.estimatedCostCents == null ? "Coste previsto pendiente" : formatMoney(row.estimatedCostCents)}</TableCell>
                        <TableCell className="text-right text-xs font-mono">{row.budgetExpectedMarginCents == null ? "Coste previsto pendiente" : formatMoney(row.budgetExpectedMarginCents)}</TableCell>
                        <TableCell className="text-right text-xs font-mono">{row.budgetExpectedMarginBps == null ? "—" : `${row.budgetExpectedMarginBps >= 0 ? "+" : ""}${(row.budgetExpectedMarginBps / 100).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} %`}</TableCell>
                        <TableCell className="text-right text-xs font-mono">{formatMoney(row.actualCostCents)}</TableCell>
                        <TableCell className="text-right text-xs font-mono">{formatMoney(row.budgetActualMarginCents)}</TableCell>
                        <TableCell className="text-right text-xs font-mono">{formatMoney(row.effectiveContractAmountCents)}</TableCell>
                        <TableCell className="text-right text-xs font-mono">{formatMoney(row.contractualActualMarginCents)} {row.contractualActualMarginBps == null ? "" : `(${row.contractualActualMarginBps >= 0 ? "+" : ""}${(row.contractualActualMarginBps / 100).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} %)`}</TableCell>
                        <TableCell className="text-right text-xs font-mono">{formatMoney(row.deviationCents)}</TableCell>
                        <TableCell><FinanceStatusBadge status={row.extractionStatus} labelOverride={budgetStatusLabel(row.extractionStatus, row.current)} /></TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button variant="ghost" size="icon" className="h-7 w-7" title="Ver líneas" aria-label="Ver líneas" onClick={() => setDetailId(row.id)}><Eye className="h-3.5 w-3.5" /></Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7" title="Abrir instalación" aria-label="Abrir instalación" onClick={() => { window.location.href = `/installations/${row.installationId}`; }}><ExternalLink className="h-3.5 w-3.5" /></Button>
                            {row.sourceFileName && <Button variant="ghost" size="icon" className="h-7 w-7" title="Descargar documento original" aria-label="Descargar documento" onClick={() => window.open(`/api/finance/budget-documents/${row.id}/source`, "_blank")}><Download className="h-3.5 w-3.5" /></Button>}
                            {canWrite && row.current && <Button variant="ghost" size="icon" className="h-7 w-7" title="Subir nueva versión" aria-label="Subir nueva versión" onClick={() => { setUploadInstallationId(row.installationId); setUploadOpen(true); }}><Upload className="h-3.5 w-3.5" /></Button>}
                            <Button variant="ghost" size="icon" className="h-7 w-7" title="Ver histórico" aria-label="Ver histórico" onClick={() => { setSearch(row.expedienteCode || String(row.installationId)); setCurrent("all"); }}><History className="h-3.5 w-3.5" /></Button>
                          </div>
                            {canWrite && <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" title="Eliminar presupuesto" aria-label="Eliminar presupuesto" onClick={() => setDeleteTarget(row)}><Trash2 className="h-3.5 w-3.5" /></Button>}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </FinanceResponsiveTable>
            )}
            {(list.data?.totalCount ?? 0) > 25 && (
              <div className="mt-4 flex items-center justify-end gap-2">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((value) => value - 1)}>Anterior</Button>
                <span className="text-xs text-muted-foreground">Página {page}</span>
                <Button variant="outline" size="sm" disabled={page * 25 >= (list.data?.totalCount ?? 0)} onClick={() => setPage((value) => value + 1)}>Siguiente</Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={deleteTarget != null} onOpenChange={(open) => { if (!open && !deleteBudget.isPending) setDeleteTarget(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Eliminar presupuesto</DialogTitle>
            <DialogDescription>
              El presupuesto {deleteTarget?.reference ? `"${deleteTarget.reference}"` : "seleccionado"} dejar&aacute; de aparecer en Finanzas. La operaci&oacute;n quedar&aacute; registrada para conservar la trazabilidad.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)} disabled={deleteBudget.isPending}>Cancelar</Button>
            <Button variant="destructive" onClick={() => deleteTarget && deleteBudget.mutate({ id: deleteTarget.id })} disabled={deleteBudget.isPending}>
              {deleteBudget.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
              Eliminar presupuesto
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <BudgetEditorDialog
        key={`upload-${uploadInstallationId ?? "new"}-${uploadOpen}`}
        open={uploadOpen}
        mode="upload"
        installations={installations.data ?? []}
        initialInstallationId={uploadInstallationId}
        onOpenChange={setUploadOpen}
        onSaved={async (id) => {
          setUploadOpen(false);
          await utils.finance.budgetDocuments.list.invalidate();
          setDetailId(id);
        }}
      />
      <BudgetEditorDialog
        open={manualOpen}
        mode="manual"
        installations={installations.data ?? []}
        onOpenChange={setManualOpen}
        onSaved={async (id) => {
          setManualOpen(false);
          await utils.finance.budgetDocuments.list.invalidate();
          setDetailId(id);
        }}
      />

      <Dialog open={detailId != null} onOpenChange={(open) => !open && setDetailId(null)}>
        <DialogContent
          className="flex max-h-none max-w-none flex-col overflow-hidden"
          style={{
            position: "fixed",
            left: detailBounds.left,
            top: detailBounds.top,
            width: detailBounds.width,
            height: detailBounds.height,
            maxWidth: "none",
            maxHeight: "none",
            transform: "none",
            translate: "none",
            boxSizing: "border-box",
          }}
        >
          <DialogHeader>
            <DialogTitle>Detalle del presupuesto</DialogTitle>
            <DialogDescription>Versión, líneas y conciliación con compras reales.</DialogDescription>
          </DialogHeader>
          <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden pr-1">
          {detail.isLoading ? (
            <Loader2 className="mx-auto my-16 h-6 w-6 animate-spin" />
          ) : detail.data ? (
            <div className="space-y-5">
              <div className="rounded-md border p-3 text-sm">
                <p className="font-medium">
                  Exp. {detail.data.installation.clientId || detail.data.installationId} · {detail.data.installation.clientName || "Sin cliente"}
                </p>
                <p className="text-muted-foreground">
                  {detail.data.installation.workDescription || "Sin descripción de obra"} · {detail.data.reference} · v{detail.data.version} · {formatDate(detail.data.budgetDate)}
                </p>
              </div>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                {[
                  ["Base", formatMoney(detail.data.sourceBaseCents)],
                  ["IVA", formatMoney(detail.data.vatCents)],
                  ["Total", formatMoney(detail.data.grossCents)],
                  ["Coste previsto", detail.data.estimatedCostCents == null ? "Coste previsto pendiente" : formatMoney(detail.data.estimatedCostCents)],
                  ["Margen previsto", detail.data.budgetExpectedMarginCents == null ? "Coste previsto pendiente" : formatMoney(detail.data.budgetExpectedMarginCents)],
                  ["Coste real", formatMoney(detail.data.actualCostCents)],
                  ["Resultado presupuesto vs coste real", formatMoney(detail.data.budgetActualMarginCents)],
                  ["Contrato vigente", formatMoney(detail.data.effectiveContractAmountCents)],
                  ["Margen contractual real", formatMoney(detail.data.contractualActualMarginCents)],
                ].map(([label, value]) => (
                  <div key={label} className="rounded-md border p-3">
                    <p className="text-xs text-muted-foreground">{label}</p>
                    <p className="mt-1 font-medium">{value}</p>
                  </div>
                ))}
              </div>
              <div className="max-w-full overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>#</TableHead><TableHead>Producto / detalle</TableHead><TableHead>Referencia</TableHead>
                    <TableHead>Unidad</TableHead><TableHead className="text-right">Cantidad</TableHead>
                    <TableHead className="text-right">Venta unit.</TableHead><TableHead className="text-right">Venta total</TableHead>
                    <TableHead className="text-right">Coste prev. unit.</TableHead><TableHead className="text-right">Coste previsto</TableHead>
                    <TableHead className="text-right">Coste real unit.</TableHead><TableHead className="text-right">Compra real</TableHead>
                    <TableHead className="text-right">Desviación</TableHead><TableHead className="text-right">Margen previsto</TableHead>
                    <TableHead className="text-right">Margen real material</TableHead><TableHead>Asociación</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {detail.data.lines.map((line: any) => {
                    const comparison = detail.data.comparison.lines.find((item: any) => item.budgetLineId === line.id);
                    return (
                      <TableRow key={line.id}>
                        <TableCell>{line.lineNumber}</TableCell>
                        <TableCell><p className="font-medium">{line.productName}</p><p className="max-w-64 text-xs text-muted-foreground">{line.description || "—"}</p></TableCell>
                        <TableCell>{line.reference || "—"}</TableCell>
                        <TableCell>{line.unit}</TableCell>
                        <TableCell className="text-right">{line.quantity}</TableCell>
                        <TableCell className="text-right">{formatMoney(line.saleUnitPriceCents)}</TableCell>
                        <TableCell className="text-right">{formatMoney(line.saleTotalCents)}</TableCell>
                        <TableCell className="text-right">{formatMoney(line.estimatedUnitCostCents)}</TableCell>
                        <TableCell className="text-right">{formatMoney(line.estimatedCostCents)}</TableCell>
                        <TableCell className="text-right">{formatMoney(
                          comparison?.purchasedQuantity
                            ? Math.round(comparison.actualCostCents / comparison.purchasedQuantity)
                            : null,
                        )}</TableCell>
                        <TableCell className="text-right">{formatMoney(comparison?.actualCostCents)}</TableCell>
                        <TableCell className="text-right">{formatMoney(comparison?.deviationCents)}</TableCell>
                        <TableCell className="text-right">{formatMoney(line.expectedMarginCents)}</TableCell>
                        <TableCell className="text-right">{formatMoney(
                          comparison ? line.saleTotalCents - comparison.actualCostCents : null,
                        )}</TableCell>
                        <TableCell>{comparison?.status || "sin asociar"} · {comparison?.matchType || "unmatched"}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              </div>
              <Card>
                <CardHeader><CardTitle className="text-base">Otros costes de la instalación</CardTitle></CardHeader>
                <CardContent className="grid gap-3 sm:grid-cols-3 text-sm">
                  <p>Técnicos: <strong>{formatMoney(detail.data.comparison.otherCosts.technicianExpensesCents)}</strong></p>
                  <p>Gastos manuales: <strong>{formatMoney(detail.data.comparison.otherCosts.manualExpensesCents)}</strong></p>
                  <p>Compras sin coincidencia: <strong>{formatMoney(detail.data.comparison.otherCosts.unmatchedPurchaseCents)}</strong></p>
                </CardContent>
              </Card>
            </div>
          ) : (
            <p className="py-10 text-center text-destructive">{detail.error?.message}</p>
          )}
          </div>
          <DialogFooter>
            {detail.data?.sourceFileName && (
              <Button variant="outline" onClick={() => window.open(`/api/finance/budget-documents/${detail.data.id}/source`, "_blank")}>
                <Download className="mr-2 h-4 w-4" />Descargar original
              </Button>
            )}
            {canWrite && detail.data?.current && (
              <Button variant="destructive" onClick={() => supersede.mutate({ id: detail.data!.id })}>
                Marcar como histórico
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

// DOC-01: exported so the contract Documentos panel can reuse the canonical
// budget upload/review/confirm flow instead of a second budget write path.
export function BudgetEditorDialog({
  open,
  mode,
  installations,
  initialInstallationId,
  onOpenChange,
  onSaved,
}: {
  open: boolean;
  mode: "upload" | "manual";
  installations: any[];
  initialInstallationId?: number | null;
  onOpenChange: (open: boolean) => void;
  onSaved: (id: number) => void;
}) {
  const [installationId, setInstallationId] = useState(initialInstallationId ? String(initialInstallationId) : "");
  const [reference, setReference] = useState("");
  const [budgetDate, setBudgetDate] = useState(new Date().toISOString().slice(0, 10));
  const [vatRate, setVatRate] = useState("21");
  const [sourceClientName, setSourceClientName] = useState("");
  const [sourceClientTaxId, setSourceClientTaxId] = useState("");
  const [sourceAddress, setSourceAddress] = useState("");
  const [notes, setNotes] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [previewSha256, setPreviewSha256] = useState("");
  const [previewPending, setPreviewPending] = useState(false);
  const [confirmPending, setConfirmPending] = useState(false);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [lines, setLines] = useState<EditableLine[]>([]);
  const [ocrBlocker, setOcrBlocker] = useState(false);
  const createManual = trpc.finance.budgetDocuments.createManual.useMutation({
    onSuccess: (result) => {
      toast.success(`Presupuesto manual guardado como versión ${result.version}`);
      onSaved(result.id);
    },
    onError: (error) => toast.error(error.message),
  });
  const busy = previewPending || confirmPending || createManual.isPending;
  const totals = useMemo(() => {
    const base = lines.reduce((sum, line) => sum + line.saleTotalCents, 0);
    const vat = Math.round(base * Number(vatRate) / 100);
    return {
      base,
      vat,
      gross: base + vat,
      cost: lines.some((line) => line.estimatedCostCents == null)
        ? null
        : lines.reduce((sum, line) => sum + (line.estimatedCostCents ?? 0), 0),
    };
  }, [lines, vatRate]);

  const addLine = () => setLines((currentLines) => [
    ...currentLines,
    {
      lineNumber: currentLines.length + 1,
      productName: "",
      costCategory: null,
      description: null,
      reference: null,
      unit: "ud",
      quantity: 1,
      saleUnitPriceCents: 0,
      saleTotalCents: 0,
      estimatedUnitCostCents: null,
      estimatedCostCents: null,
      reviewStatus: "confirmed",
      warnings: [],
    },
  ]);
  const updateLine = (index: number, patch: Partial<EditableLine>) => setLines((currentLines) => (
    currentLines.map((line, lineIndex) => lineIndex === index ? { ...line, ...patch } : line)
  ));

  const inspectFile = async () => {
    if (!file || !installationId) return toast.error("Selecciona instalación y documento");
    if (file.size > FINANCE_BUDGET_MAX_FILE_BYTES) return toast.error("El archivo supera 25 MB");
    setPreviewPending(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("installationId", installationId);
      form.append("budgetDate", budgetDate);
      form.append("reference", reference);
      if (notes) form.append("notes", notes);
      const result = await postBudgetMultipart("preview", form);
      setPreviewSha256(result.sha256 ?? "");
      setWarnings(result.warnings ?? []);
      setOcrBlocker(result.errorCode === "OCR_PROVIDER_NOT_CONFIGURED");
      if (result.status === "failed") {
        setLines([]);
        return;
      }
      setReference(result.reference || reference);
      setBudgetDate(result.budgetDate || budgetDate);
      setVatRate(String([0, 4, 10, 21].includes(result.vatRate) ? result.vatRate : 21));
      setSourceClientName(result.sourceClientName || "");
      setSourceClientTaxId(result.sourceClientTaxId || "");
      setSourceAddress(result.sourceAddress || "");
      setLines(result.lines as EditableLine[]);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "No se pudo extraer el documento");
    } finally {
      setPreviewPending(false);
    }
  };
  const save = async () => {
    if (!installationId || !reference.trim() || !budgetDate || lines.length === 0) {
      toast.error("Completa instalación, referencia, fecha y al menos una línea");
      return;
    }
    if (lines.some((line) => !line.productName.trim() || line.quantity <= 0)) {
      toast.error("Revisa el nombre y la cantidad de todas las líneas");
      return;
    }
    const reviewed = lines.map((line, index) => ({ ...line, lineNumber: index + 1, reviewStatus: "confirmed" as const }));
    if (mode === "manual") {
      createManual.mutate({
        installationId: Number(installationId),
        reference,
        budgetDate,
        vatRate: Number(vatRate) as 0 | 4 | 10 | 21,
        sourceClientName: sourceClientName || null,
        sourceClientTaxId: sourceClientTaxId || null,
        sourceAddress: sourceAddress || null,
        notes: notes || null,
        lines: reviewed,
      });
      return;
    }
    if (!file || !previewSha256) return toast.error("Extrae y revisa el documento antes de guardar");
    setConfirmPending(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("confirm", "SAVE_REVIEWED_FINANCE_BUDGET");
      form.append("reviewedPayload", JSON.stringify({
        installationId: Number(installationId),
        reference,
        budgetDate,
        vatRate: Number(vatRate),
        sourceClientName: sourceClientName || null,
        sourceClientTaxId: sourceClientTaxId || null,
        sourceAddress: sourceAddress || null,
        notes: notes || null,
        extractionWarnings: warnings,
        lines: reviewed,
        previewSha256,
      }));
      const result = await postBudgetMultipart("confirm", form);
      toast.success(`Presupuesto guardado como versión ${result.version}`);
      onSaved(result.id);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "No se pudo guardar el presupuesto");
    } finally {
      setConfirmPending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-6xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{mode === "upload" ? "Subir presupuesto" : "Crear presupuesto manual"}</DialogTitle>
          <DialogDescription>
            {mode === "upload"
              ? "DOCX o PDF, máximo 25 MB. La extracción nunca se guarda sin revisión humana."
              : "Introduce las líneas y costes conocidos. Los costes ausentes permanecen sin calcular."}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 md:grid-cols-4">
          <div className="space-y-1 md:col-span-2">
            <Label>Instalación *</Label>
            <Select value={installationId} onValueChange={setInstallationId}>
              <SelectTrigger className="w-full"><SelectValue placeholder="Selecciona un expediente" /></SelectTrigger>
              <SelectContent>
                {installations.map((installation) => (
                  <SelectItem key={installation.id} value={String(installation.id)}>
                    Exp. {installation.clientId || installation.id} · {installation.clientName || installation.workDescription || "Sin nombre"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Referencia *</Label>
            <Input value={reference} onChange={(event) => setReference(event.target.value)} />
          </div>
          <div className="space-y-1">
            <Label>Fecha *</Label>
            <Input type="date" value={budgetDate} onChange={(event) => setBudgetDate(event.target.value)} />
          </div>
          {mode === "upload" && (
            <div className="space-y-1 md:col-span-3">
              <Label>Documento original *</Label>
              <Input
                type="file"
                accept=".docx,.pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/pdf"
                onChange={(event) => {
                  setFile(event.target.files?.[0] ?? null);
                  setPreviewSha256("");
                  setLines([]);
                }}
              />
            </div>
          )}
          <div className="space-y-1">
            <Label>IVA (%)</Label>
            <Select value={vatRate} onValueChange={setVatRate}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {[0, 4, 10, 21].map((rate) => <SelectItem key={rate} value={String(rate)}>{rate} %</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Cliente del documento</Label>
            <Input value={sourceClientName} onChange={(event) => setSourceClientName(event.target.value)} />
          </div>
          <div className="space-y-1">
            <Label>CIF/NIF del documento</Label>
            <Input value={sourceClientTaxId} onChange={(event) => setSourceClientTaxId(event.target.value)} />
          </div>
          <div className="space-y-1 md:col-span-2">
            <Label>Dirección del documento</Label>
            <Input value={sourceAddress} onChange={(event) => setSourceAddress(event.target.value)} />
          </div>
          <div className="space-y-1 md:col-span-4">
            <Label>Notas</Label>
            <Textarea value={notes} onChange={(event) => setNotes(event.target.value)} placeholder="Observaciones opcionales" />
          </div>
        </div>
        {mode === "upload" && (
          <Button variant="outline" onClick={inspectFile} disabled={busy || !file || !installationId}>
            {previewPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Eye className="mr-2 h-4 w-4" />}
            Extraer para revisión
          </Button>
        )}
        {ocrBlocker && (
          <div className="rounded-md border border-amber-300 bg-amber-50 p-4 text-sm text-amber-950">
            <p className="font-medium">OCR_PROVIDER_NOT_CONFIGURED</p>
            <p className="mt-1">El PDF parece escaneado y no existe un proveedor OCR autorizado. Créalo manualmente o usa un documento con texto seleccionable.</p>
          </div>
        )}
        {warnings.length > 0 && !ocrBlocker && (
          <div className="rounded-md border bg-muted/40 p-3 text-xs text-muted-foreground">
            Revisión requerida: {warnings.join(" · ")}
          </div>
        )}
        {(mode === "manual" || lines.length > 0) && (
          <div className="min-w-0 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Líneas revisables</p>
                <p className="text-xs text-muted-foreground">
                  Base {formatMoney(totals.base)} · IVA {formatMoney(totals.vat)} · Total {formatMoney(totals.gross)} · {totals.cost == null ? "Coste previsto pendiente" : `Coste previsto ${formatMoney(totals.cost)}`}
                </p>
              </div>
              <Button variant="outline" size="sm" onClick={addLine}><Plus className="mr-2 h-4 w-4" />Añadir línea</Button>
            </div>
            <div className="hidden max-w-full overflow-x-auto md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Producto *</TableHead><TableHead>Categoría de coste</TableHead><TableHead>Descripción</TableHead><TableHead>Ref.</TableHead>
                  <TableHead>Unidad</TableHead><TableHead>Cantidad</TableHead>
                  <TableHead>Precio venta</TableHead><TableHead>Total venta</TableHead>
                  <TableHead>Coste unit.</TableHead><TableHead>Coste total</TableHead><TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {lines.map((line, index) => (
                  <TableRow key={`${line.lineNumber}-${index}`}>
                    <TableCell>
                      <Input className="min-w-44" value={line.productName} onChange={(e) => updateLine(index, { productName: e.target.value })} />
                      {line.warnings.length > 0 && <p className="mt-1 max-w-44 text-xs text-amber-700">{line.warnings.join(" · ")}</p>}
                    </TableCell>
                    <TableCell><select aria-label="Categoría de coste" className="h-9 min-w-36 rounded-md border bg-background px-2 text-sm" value={line.costCategory ?? ""} onChange={(e) => updateLine(index, { costCategory: (e.target.value || null) as EditableLine["costCategory"] })}><option value="">Sin clasificar</option><option value="materials">Materiales</option><option value="labor">Mano de obra</option><option value="other">Otros</option></select></TableCell>
                    <TableCell><Textarea className="min-w-52" value={line.description ?? ""} onChange={(e) => updateLine(index, { description: e.target.value || null })} /></TableCell>
                    <TableCell><Input className="w-28" value={line.reference ?? ""} onChange={(e) => updateLine(index, { reference: e.target.value || null })} /></TableCell>
                    <TableCell><Input className="w-20" value={line.unit} onChange={(e) => updateLine(index, { unit: e.target.value })} /></TableCell>
                    <TableCell><Input className="w-24" type="number" min="0.0001" step="0.01" value={line.quantity} onChange={(e) => {
                      const quantity = Number(e.target.value);
                      updateLine(index, {
                        quantity,
                        saleTotalCents: line.saleUnitPriceCents == null ? line.saleTotalCents : Math.round(line.saleUnitPriceCents * quantity),
                        estimatedCostCents: line.estimatedUnitCostCents == null ? null : Math.round(line.estimatedUnitCostCents * quantity),
                      });
                    }} /></TableCell>
                    <TableCell><Input className="w-28" value={moneyInput(line.saleUnitPriceCents)} onChange={(e) => {
                      const unit = centsFromInput(e.target.value);
                      updateLine(index, { saleUnitPriceCents: unit, saleTotalCents: unit == null ? line.saleTotalCents : Math.round(unit * line.quantity) });
                    }} /></TableCell>
                    <TableCell><Input className="w-28" value={moneyInput(line.saleTotalCents)} onChange={(e) => updateLine(index, { saleTotalCents: centsFromInput(e.target.value) ?? 0 })} /></TableCell>
                    <TableCell><Input className="w-28" value={moneyInput(line.estimatedUnitCostCents)} onChange={(e) => {
                      const unit = centsFromInput(e.target.value);
                      updateLine(index, { estimatedUnitCostCents: unit, estimatedCostCents: unit == null ? null : Math.round(unit * line.quantity) });
                    }} /></TableCell>
                    <TableCell><Input className="w-28" value={moneyInput(line.estimatedCostCents)} onChange={(e) => updateLine(index, { estimatedCostCents: e.target.value ? centsFromInput(e.target.value) : null })} /></TableCell>
                    <TableCell><Button variant="ghost" size="sm" onClick={() => setLines((all) => all.filter((_, i) => i !== index))}>Quitar</Button></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>
            <div className="space-y-3 md:hidden" aria-label="Líneas del presupuesto">
              {lines.map((line, index) => (
                <section
                  key={`mobile-${line.lineNumber}-${index}`}
                  className="min-w-0 space-y-3 border-b pb-4"
                  aria-label={`Línea ${index + 1}`}
                >
                  <div className="grid min-w-0 gap-3">
                    <div className="min-w-0 space-y-1">
                      <Label htmlFor={`budget-product-${index}`}>Producto *</Label>
                      <Input id={`budget-product-${index}`} className="w-full min-w-0" value={line.productName} onChange={(e) => updateLine(index, { productName: e.target.value })} />
                      {line.warnings.length > 0 && <p className="text-xs text-amber-700">{line.warnings.join(" · ")}</p>}
                    </div>
                    <div className="min-w-0 space-y-1">
                      <Label htmlFor={`budget-category-${index}`}>Categoría de coste</Label>
                      <select id={`budget-category-${index}`} aria-label="Categoría de coste" className="h-11 w-full min-w-0 rounded-md border bg-background px-3 text-sm" value={line.costCategory ?? ""} onChange={(e) => updateLine(index, { costCategory: (e.target.value || null) as EditableLine["costCategory"] })}><option value="">Sin clasificar</option><option value="materials">Materiales</option><option value="labor">Mano de obra</option><option value="other">Otros</option></select>
                    </div>
                    <div className="min-w-0 space-y-1">
                      <Label htmlFor={`budget-description-${index}`}>Descripción</Label>
                      <Textarea id={`budget-description-${index}`} className="w-full min-w-0" value={line.description ?? ""} onChange={(e) => updateLine(index, { description: e.target.value || null })} />
                    </div>
                    <div className="grid min-w-0 grid-cols-2 gap-3">
                      <div className="min-w-0 space-y-1"><Label htmlFor={`budget-reference-${index}`}>Referencia</Label><Input id={`budget-reference-${index}`} className="w-full min-w-0" value={line.reference ?? ""} onChange={(e) => updateLine(index, { reference: e.target.value })} /></div>
                      <div className="min-w-0 space-y-1"><Label htmlFor={`budget-unit-${index}`}>Unidad</Label><Input id={`budget-unit-${index}`} className="w-full min-w-0" value={line.unit} onChange={(e) => updateLine(index, { unit: e.target.value })} /></div>
                      <div className="min-w-0 space-y-1"><Label htmlFor={`budget-quantity-${index}`}>Cantidad</Label><Input id={`budget-quantity-${index}`} className="w-full min-w-0" type="number" min="0.0001" step="0.01" value={line.quantity} onChange={(e) => {
                        const quantity = Number(e.target.value);
                        updateLine(index, {
                          quantity,
                          saleTotalCents: line.saleUnitPriceCents == null ? line.saleTotalCents : Math.round(line.saleUnitPriceCents * quantity),
                          estimatedCostCents: line.estimatedUnitCostCents == null ? null : Math.round(line.estimatedUnitCostCents * quantity),
                        });
                      }} /></div>
                    </div>
                    <div className="grid min-w-0 grid-cols-2 gap-3">
                      <div className="min-w-0 space-y-1"><Label htmlFor={`budget-sale-unit-${index}`}>Precio venta / unidad</Label><Input id={`budget-sale-unit-${index}`} className="w-full min-w-0" inputMode="decimal" value={moneyInput(line.saleUnitPriceCents)} onChange={(e) => {
                        const unit = centsFromInput(e.target.value);
                        updateLine(index, { saleUnitPriceCents: unit, saleTotalCents: unit == null ? line.saleTotalCents : Math.round(unit * line.quantity) });
                      }} /></div>
                      <div className="min-w-0 space-y-1"><Label htmlFor={`budget-sale-total-${index}`}>Total venta</Label><Input id={`budget-sale-total-${index}`} className="w-full min-w-0" inputMode="decimal" value={moneyInput(line.saleTotalCents)} onChange={(e) => updateLine(index, { saleTotalCents: centsFromInput(e.target.value) ?? 0 })} /></div>
                      <div className="min-w-0 space-y-1"><Label htmlFor={`budget-cost-unit-${index}`}>Coste previsto / unidad</Label><Input id={`budget-cost-unit-${index}`} className="w-full min-w-0" inputMode="decimal" value={moneyInput(line.estimatedUnitCostCents)} onChange={(e) => {
                        const unit = centsFromInput(e.target.value);
                        updateLine(index, { estimatedUnitCostCents: unit, estimatedCostCents: unit == null ? null : Math.round(unit * line.quantity) });
                      }} /></div>
                      <div className="min-w-0 space-y-1"><Label htmlFor={`budget-cost-total-${index}`}>Coste previsto total</Label><Input id={`budget-cost-total-${index}`} className="w-full min-w-0" inputMode="decimal" value={moneyInput(line.estimatedCostCents)} onChange={(e) => updateLine(index, { estimatedCostCents: e.target.value ? centsFromInput(e.target.value) : null })} /></div>
                    </div>
                    <Button type="button" variant="outline" className="min-h-11 w-full" onClick={() => setLines((all) => all.filter((_, i) => i !== index))}><Trash2 className="mr-2 h-4 w-4" />Quitar línea</Button>
                  </div>
                </section>
              ))}
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={save} disabled={busy || ocrBlocker || lines.length === 0}>
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Guardar versión revisada
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
